import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
const createEmptyMappingRow = (id) => ({
    id: String(id),
    orgId: '',
    value: '',
    isExisting: false,
});
export const useOrgMappings = ({ isEditMode, variableData, orgList, isModalOpened, sensitive, }) => {
    const [orgMappings, setOrgMappings] = useState([
        createEmptyMappingRow(1),
    ]);
    const [rowErrors, setRowErrors] = useState({});
    const originalOrgIdentifiersRef = useRef(new Set());
    const initialValuesRef = useRef(new Map());
    const { idToNameMap, nameToIdMap } = useMemo(() => {
        const idToName = new Map();
        const nameToId = new Map();
        orgList?.forEach(org => {
            const id = String(org.orgId);
            idToName.set(id, org.orgName);
            nameToId.set(org.orgName, id);
        });
        return { idToNameMap: idToName, nameToIdMap: nameToId };
    }, [orgList]);
    const resolveOrgIdentifier = useCallback((orgId) => idToNameMap.get(orgId) || orgId, [idToNameMap]);
    useEffect(() => {
        if (isEditMode && variableData) {
            if (variableData.values && variableData.values.length > 0) {
                const orgScopedValues = variableData.values.filter((v) => v.org_identifier &&
                    (!v.principal_type ||
                        (v.principal_type !== 'USER' &&
                            v.principal_type !== 'USER_GROUP' &&
                            v.principal_type !== 'OBJECT')));
                const mappings = orgScopedValues.map((val, idx) => {
                    let valueStr = '';
                    if (val.value_list && Array.isArray(val.value_list)) {
                        valueStr = val.value_list.join(';');
                    }
                    else if (val.value) {
                        valueStr = val.value;
                    }
                    return {
                        id: String(idx + 1),
                        orgId: '',
                        orgIdentifier: val.org_identifier || '',
                        value: valueStr,
                        isExisting: true,
                    };
                });
                originalOrgIdentifiersRef.current = new Set(mappings
                    .map((mapping) => mapping.orgIdentifier)
                    .filter((identifier) => Boolean(identifier)));
                const initialValues = new Map();
                mappings.forEach((mapping) => {
                    if (mapping.orgIdentifier && mapping.isExisting) {
                        initialValues.set(mapping.orgIdentifier, mapping.value || '');
                    }
                });
                initialValuesRef.current = initialValues;
                setOrgMappings(mappings.length > 0 ? mappings : [createEmptyMappingRow(1)]);
            }
            else {
                setOrgMappings([createEmptyMappingRow(1)]);
                originalOrgIdentifiersRef.current = new Set();
                initialValuesRef.current = new Map();
            }
        }
        else if (!isEditMode) {
            setOrgMappings([createEmptyMappingRow(1)]);
            originalOrgIdentifiersRef.current = new Set();
            initialValuesRef.current = new Map();
        }
    }, [isEditMode, variableData, sensitive]);
    useEffect(() => {
        if (!isEditMode || nameToIdMap.size === 0) {
            return;
        }
        setOrgMappings(currentMappings => {
            let updatedMappings = null;
            currentMappings.forEach((mapping, index) => {
                if (!mapping.orgIdentifier || mapping.orgId) {
                    return;
                }
                const resolvedId = nameToIdMap.get(mapping.orgIdentifier);
                if (!resolvedId) {
                    return;
                }
                if (!updatedMappings) {
                    updatedMappings = [...currentMappings];
                }
                updatedMappings[index] = {
                    ...mapping,
                    orgId: resolvedId,
                };
            });
            return updatedMappings || currentMappings;
        });
    }, [isEditMode, nameToIdMap]);
    useEffect(() => {
        if (isModalOpened) {
            setRowErrors({});
        }
        else {
            if (!isEditMode) {
                setOrgMappings([createEmptyMappingRow(1)]);
            }
            originalOrgIdentifiersRef.current = new Set();
            setRowErrors({});
        }
    }, [isModalOpened, isEditMode]);
    const handleAddRow = useCallback(() => {
        setOrgMappings(prev => [
            ...prev,
            createEmptyMappingRow(prev.length + 1),
        ]);
    }, []);
    const handleRemoveRow = useCallback((id) => {
        setOrgMappings(prev => {
            if (prev.length <= 1) {
                return prev;
            }
            return prev.filter(mapping => mapping.id !== id);
        });
        setRowErrors(prev => {
            if (!prev[id]) {
                return prev;
            }
            const next = { ...prev };
            delete next[id];
            return next;
        });
    }, []);
    const handleMappingNameChange = useCallback((id, orgId) => {
        const orgIdentifier = orgId ? resolveOrgIdentifier(orgId) : '';
        setOrgMappings(prev => prev.map(mapping => mapping.id === id
            ? { ...mapping, orgId, orgIdentifier }
            : mapping));
        setRowErrors(prev => {
            if (!prev[id]?.orgId) {
                return prev;
            }
            return {
                ...prev,
                [id]: { ...prev[id], orgId: false },
            };
        });
    }, [resolveOrgIdentifier]);
    const handleValueChange = useCallback((id, value) => {
        setOrgMappings(prev => prev.map(mapping => mapping.id === id ? { ...mapping, value } : mapping));
        setRowErrors(prev => {
            if (!prev[id]?.value) {
                return prev;
            }
            return {
                ...prev,
                [id]: { ...prev[id], value: false },
            };
        });
    }, []);
    const validateOrgMappingRows = useCallback(() => {
        const errors = {};
        let hasError = false;
        orgMappings.forEach(mapping => {
            const rowError = {};
            const hasOrgId = mapping.orgId && mapping.orgId.trim() !== '';
            const hasValue = mapping.value && mapping.value.trim() !== '';
            const isExistingSensitiveValue = sensitive && mapping.isExisting && !mapping.value;
            if (!hasOrgId && !hasValue && !isExistingSensitiveValue) {
                return;
            }
            if (!hasOrgId) {
                rowError.orgId = true;
                hasError = true;
            }
            if (!hasValue && !isExistingSensitiveValue) {
                rowError.value = true;
                hasError = true;
            }
            if (rowError.orgId || rowError.value) {
                errors[mapping.id] = rowError;
            }
        });
        setRowErrors(errors);
        return !hasError;
    }, [orgMappings, sensitive]);
    const hasValidContent = useMemo(() => {
        return orgMappings.some(mapping => {
            const hasOrgId = mapping.orgId && mapping.orgId.trim() !== '';
            const hasValue = mapping.value && mapping.value.trim() !== '';
            return hasOrgId || hasValue;
        });
    }, [orgMappings]);
    const hasChanges = useMemo(() => {
        if (!isEditMode) {
            return true;
        }
        const hasNewRowsWithContent = () => {
            return orgMappings.some(mapping => {
                if (mapping.isExisting) {
                    return false;
                }
                const hasOrgId = mapping.orgId && mapping.orgId.trim() !== '';
                const hasValue = mapping.value && mapping.value.trim() !== '';
                return hasOrgId || hasValue;
            });
        };
        const hasRemovedRows = () => {
            const currentOrgIdentifiers = new Set(orgMappings
                .map(m => m.orgIdentifier)
                .filter((id) => Boolean(id)));
            return Array.from(originalOrgIdentifiersRef.current).some(originalId => !currentOrgIdentifiers.has(originalId));
        };
        const hasModifiedValues = () => {
            return orgMappings.some(mapping => {
                if (!mapping.isExisting || !mapping.orgIdentifier) {
                    return false;
                }
                if (sensitive && !mapping.value) {
                    return false;
                }
                const originalValue = initialValuesRef.current.get(mapping.orgIdentifier);
                const currentValue = mapping.value || '';
                return originalValue !== currentValue;
            });
        };
        return (hasNewRowsWithContent() || hasRemovedRows() || hasModifiedValues());
    }, [isEditMode, orgMappings, sensitive]);
    return {
        orgMappings,
        rowErrors,
        handleAddRow,
        handleRemoveRow,
        handleMappingNameChange,
        handleValueChange,
        validateOrgMappingRows,
        idToNameMap,
        nameToIdMap,
        resolveOrgIdentifier,
        originalOrgIdentifiersRef,
        setOrgMappings,
        setRowErrors,
        hasChanges,
        hasValidContent,
    };
};
//# sourceMappingURL=useOrgMappings.js.map